import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import 'react-toastify/dist/ReactToastify.css';
import { ValidateTokenUrl, ResetChangePasswordUrl } from './serverurl';

function PasswordResetForm() {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [errors, setErrors] = useState({});
  const [tokenValid, setTokenValid] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const urlToken = searchParams.get('token');
    const urlEmail = searchParams.get('email');

    if (urlToken && urlEmail) {
      setToken(urlToken);
      setEmail(urlEmail);
      validateToken(urlToken, urlEmail);
    } else {
      setTokenValid(false);
      setErrors({ general: 'Invalid reset link' });
    }
  }, []);

  const validateToken = async (token, email) => {
    try {
      await axios.get(ValidateTokenUrl, {
        params: { token, email },
      });
      setTokenValid(true);
    } catch (error) {
      setTokenValid(false);
      const errorMessage =
        error.response?.data?.message || 'Token validation failed';
      setErrors({ token: errorMessage });
      toast.error(`❌ ${errorMessage}`);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!password) newErrors.password = 'Password is required';
    else if (password.length < 8)
      newErrors.password = 'Password must be at least 8 characters';
    if (password !== passwordConfirmation)
      newErrors.passwordConfirmation = 'Passwords must match';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setProcessing(true);
    try {
      await axios.post(ResetChangePasswordUrl, {
        token,
        email,
        password,
        password_confirmation: passwordConfirmation,
      });
      toast.success('✅ Password reset successfully! Redirecting to login...');
      setSuccess(true);
      setTimeout(() => (window.location.href = '/login'), 3000);
    } catch (error) {
      toast.error(
        error.response?.data?.message || '❌ Password reset failed'
      );
    } finally {
      setProcessing(false);
    }
  };

  if (tokenValid === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-gray-700">
        <svg
          className="animate-spin h-6 w-6 text-blue-500"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          ></path>
        </svg>
        <span className="mt-3">Verifying token...</span>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white shadow-md rounded-lg p-6 text-center">
          <h2 className="text-red-600 text-xl font-semibold mb-4">
            ⚠️ Invalid Token
          </h2>
          <p className="mb-4">
            {errors.token ||
              'This password reset link is invalid or has expired.'}
          </p>
          <a
            href="/password-reset"
            className="inline-block bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            ↻ Request New Reset Link
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Reset Password - cbthost.com</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center px-4 py-6">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <h2 className="text-center text-2xl font-semibold mb-4">
            🔒 Reset Your Password
          </h2>

          {success ? (
            <div className="bg-green-100 border border-green-300 text-green-700 px-4 py-3 rounded text-center">
              <h4 className="font-bold">🎉 Password Changed!</h4>
              <p>Redirecting to login page...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block mb-1 font-medium">
                  New Password
                </label>
                <input
                  type="password"
                  className={`w-full px-3 py-2 border rounded outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                />
                {errors.password && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.password}
                  </p>
                )}
              </div>

              <div>
                <label className="block mb-1 font-medium">
                  Confirm Password
                </label>
                <input
                  type="password"
                  className={`w-full px-3 py-2 border rounded outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.passwordConfirmation
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                  value={passwordConfirmation}
                  onChange={(e) => setPasswordConfirmation(e.target.value)}
                  placeholder="Confirm new password"
                />
                {errors.passwordConfirmation && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.passwordConfirmation}
                  </p>
                )}
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition flex items-center justify-center"
                disabled={processing}
              >
                {processing && (
                  <svg
                    className="animate-spin h-5 w-5 mr-2 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    ></path>
                  </svg>
                )}
                {processing ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </form>
          )}
        </div>
      </div>

      <ToastContainer position="top-center" />
    </>
  );
}

export default PasswordResetForm;
